"use client";

import { useRef, useState } from "react";
import { track } from "@vercel/analytics";
import { useLocale } from "@/lib/i18n";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Notes subscribe form. POSTs { email, company_url } to /api/waitlist (which
 * stores the signup + sends a welcome email, honeypot + rate-limited). Trilingual
 * via the waitlist dictionary. The hidden company_url field is the honeypot.
 */
export function NewsletterForm() {
  const { t } = useLocale();
  const w = t.waitlist;
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "done">("idle");
  const [error, setError] = useState("");
  const hpRef = useRef<HTMLInputElement>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;
    setError("");
    if (!EMAIL_RE.test(email.trim())) {
      setError(w.errorMsg);
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim(), company_url: hpRef.current?.value ?? "" }),
      });
      const j = await res.json().catch(() => ({ ok: false }));
      if (res.ok && j.ok) {
        track("newsletter_subscribed", { source: "notes" });
        setStatus("done");
      } else {
        setStatus("idle");
        setError(j.error || w.errorMsg);
      }
    } catch {
      setStatus("idle");
      setError(w.errorMsg);
    }
  }

  return (
    <section
      aria-label={w.headline}
      className="relative mx-auto max-w-[760px] px-[var(--spacing-page-x)] py-12 border-t border-[var(--color-hairline)]"
    >
      <div className="rounded-lg border border-[var(--color-hairline)] bg-[color:rgba(59,130,246,0.04)] p-7 lg:p-9">
        <div className="microcaps mb-3 text-[var(--color-text-mute)]">{w.micro}</div>
        <h2
          className="mb-2 text-2xl lg:text-3xl text-[var(--color-text)]"
          style={{ fontFamily: "var(--font-display)", fontVariationSettings: '"wght" 700' }}
        >
          {w.headline}
        </h2>
        <p className="mb-6 max-w-[520px] text-[var(--color-text-dim)]">{w.subhead}</p>

        {status === "done" ? (
          <p role="status" aria-live="polite" className="text-[var(--color-blue)]">
            ✓ {w.successMsg}
          </p>
        ) : (
          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <input
              ref={hpRef}
              type="text"
              name="company_url"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden
              className="absolute -left-[9999px] h-px w-px overflow-hidden"
            />
            <div className="flex-1">
              <label htmlFor="nl-email" className="sr-only">
                {w.placeholder}
              </label>
              <input
                id="nl-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={w.placeholder}
                aria-describedby="nl-error"
                className="w-full rounded-md border border-[var(--color-hairline)] bg-[color:rgba(255,255,255,0.03)] px-4 py-3 text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-blue)]"
              />
              <div id="nl-error" role="alert" aria-live="assertive" className="mt-2 min-h-[18px] text-sm text-[#f0a5a5]">
                {error}
              </div>
            </div>
            <button
              type="submit"
              disabled={status === "submitting"}
              className="shrink-0 rounded-md bg-[var(--color-blue)] px-5 py-3 microcaps text-[var(--color-navy)] transition-[filter] hover:brightness-110 disabled:opacity-60"
            >
              {status === "submitting" ? w.submitting : w.cta}
            </button>
          </form>
        )}
        <p className="mt-4 text-sm text-[var(--color-text-mute)]">{w.privacyNote}</p>
      </div>
    </section>
  );
}
